import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/posthog";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ShieldCheck, Lock, Users, Wallet, BookOpen, CalendarDays, FileBarChart,
  LineChart, Building2, ArrowRight, Mail, Phone, MapPin,
  FileText, Layers, ScrollText, CheckCircle2, Sparkles, Briefcase, Coins,
  Boxes, Zap, GitBranch, Rocket,
} from "lucide-react";

const NAVY = "#0b192c";
const GOLD = "#c4a454";
const CREAM = "#fdfcf8";
const BORDER = "#e2e1dd";
const MUTED = "#475569";

/* ───────── Platform logo + wordmark (Owner-configurable) ─────────
 * Fetches /api/platform/brand and swaps into <img> tags. Kept in this
 * file (rather than imported from components/brand/PlatformWordmark) so
 * the public landing page stays usable when the user is logged-out and
 * AuthProvider hasn't yet resolved.
 */
function usePublicBrand() {
  const [brand, setBrand] = React.useState({
    logo_url: "/corpzo-icon-sm.png", wordmark_url: null,
  });
  React.useEffect(() => {
    let cancelled = false;
    const apply = () => api.get("/platform/brand")
      .then(({ data }) => { if (!cancelled && data) setBrand({
        logo_url: data.logo_url || "/corpzo-icon-sm.png",
        wordmark_url: data.wordmark_url || null,
      }); })
      .catch(() => {});
    apply();
    const h = () => apply();
    window.addEventListener("aif-platform-brand-updated", h);
    return () => { cancelled = true; window.removeEventListener("aif-platform-brand-updated", h); };
  }, []);
  return brand;
}

function PlatformLogo({ className = "h-9 w-9", width = 36, height = 36, alt = "AIFLedger" }) {
  const { logo_url } = usePublicBrand();
  return (
    <img src={logo_url} alt={alt} width={width} height={height}
         className={`${className} object-contain`} data-testid="platform-brand-mark" />
  );
}
function PlatformWordmarkImg({ className = "h-7" }) {
  const { wordmark_url } = usePublicBrand();
  if (!wordmark_url) {
    return (
      <span className="font-serif text-xl leading-none" style={{ color: NAVY }}>
        AIFLedger<span style={{ color: GOLD }}>.</span>
      </span>
    );
  }
  return <img src={wordmark_url} alt="AIFLedger" className={`${className} object-contain`}
              data-testid="platform-brand-wordmark" />;
}

/* ───────────────── CMS sections (Owner-editable via /owner/landing) ───────────────── */

// Deterministic 50/50 bucketing keyed off a stable client id in localStorage.
// Same visitor always sees the same variant across refreshes.
function _abBucket(splitPct = 50) {
  let sid;
  try {
    sid = localStorage.getItem("aifl_ab_sid");
    if (!sid) {
      sid = Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
      localStorage.setItem("aifl_ab_sid", sid);
    }
  } catch { sid = "anon"; }
  let h = 0;
  for (let i = 0; i < sid.length; i++) h = ((h << 5) - h + sid.charCodeAt(i)) | 0;
  return (Math.abs(h) % 100) < splitPct ? "A" : "B";
}

function _sessionId() {
  try { return localStorage.getItem("aifl_ab_sid") || "anon"; } catch { return "anon"; }
}

// Exposed so the lead-form success handler can call it too.
export async function trackLandingEvent(event) {
  try {
    const v = window.__aifl_ab_variant || "A";
    await api.post("/landing/track", { variant: v, event, session_id: _sessionId() });
  } catch { /* silent */ }
}

function LandingCMSSections() {
  const [cfg, setCfg] = useState(null);
  const [variant, setVariant] = useState("A");
  useEffect(() => {
    api.get("/landing/config").then((r) => setCfg(r.data)).catch(() => setCfg({ sections: {} }));
  }, []);
  useEffect(() => {
    if (!cfg) return;
    const ab = cfg.ab_experiment || {};
    const v = ab.enabled ? _abBucket(ab.split_pct ?? 50) : "A";
    window.__aifl_ab_variant = v;
    setVariant(v);
    // Fire a view event once per mount when case_study is visible + A/B on.
    if (ab.enabled && cfg.sections?.case_study?.enabled) {
      trackLandingEvent("view");
    }
  }, [cfg]);
  if (!cfg) return null;
  const s = cfg.sections || {};
  const stats = cfg.trust_stats || [];
  // Which case-study copy to render (variant A = default, B = experiment).
  const csEnabled = s.case_study?.enabled;
  const cs = variant === "B" && cfg.ab_experiment?.variant_b
             ? cfg.ab_experiment.variant_b
             : cfg.case_study || {};

  // Section render map \u2014 keys match cfg.section_order.
  const renderers = {
    trust_stats: () => s.trust_stats?.enabled && stats.length > 0 && (
      <section key="trust" className="border-y" style={{ borderColor: BORDER, background: "white" }}
               data-testid="landing-trust-strip" aria-label="AIFLedger scale numbers">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
          <div className="text-center mb-8">
            <div className="text-[11px] uppercase tracking-[0.25em] font-semibold" style={{ color: GOLD }}>
              Trusted by India&rsquo;s AIF managers
            </div>
            <div className="font-serif text-2xl sm:text-3xl mt-2" style={{ color: NAVY }}>
              The numbers behind the platform.
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((st) => (
              <div key={st.key} className="text-center md:text-left" data-testid={`trust-stat-${st.key}`}>
                <div className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-none tracking-tight"
                     style={{ color: NAVY }}>{st.value}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] mt-3 font-semibold"
                     style={{ color: NAVY }}>{st.label}</div>
                <div className="text-[11px] mt-1.5 leading-snug" style={{ color: MUTED }}>{st.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    ),
    logo_wall: () => s.logo_wall?.enabled && (cfg.logo_wall?.logos || []).length > 0 && (
      <section key="logo" className="border-b overflow-hidden" style={{ borderColor: BORDER, background: "#faf9f5" }}
               data-testid="landing-logo-wall" aria-label="Managed AIF practices">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-2">
          <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-center" style={{ color: MUTED }}>
            {cfg.logo_wall?.eyebrow || "Managing money for"}
          </div>
        </div>
        <div className="relative py-8">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-20 z-10"
               style={{ background: "linear-gradient(to right,#faf9f5,transparent)" }} />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-20 z-10"
               style={{ background: "linear-gradient(to left,#faf9f5,transparent)" }} />
          <div className="flex gap-14 whitespace-nowrap marquee-track">
            {[...cfg.logo_wall.logos, ...cfg.logo_wall.logos].map((lg, i) => (
              <div key={i} className="flex items-center gap-3 flex-shrink-0"
                   data-testid={i < cfg.logo_wall.logos.length ? `logo-${i}` : undefined}>
                {lg.src ? (
                  <img src={lg.src} alt={lg.name} className="h-9 opacity-70 hover:opacity-100 transition"
                       style={{ maxWidth: 160, objectFit: "contain" }} />
                ) : (
                  <div className="font-serif text-xl tracking-tight opacity-60 hover:opacity-100 transition"
                       style={{ color: NAVY }}>{lg.name}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    ),
    case_study: () => csEnabled && (cs.cards || []).length > 0 && (
      <section key="case" className="border-b" style={{ borderColor: BORDER, background: NAVY }}
               data-testid="landing-case-study" data-variant={variant} aria-label="Customer case study">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.28em] font-semibold flex items-center gap-3"
                 style={{ color: GOLD }}>
              <span className="inline-block w-8 h-[1px]" style={{ background: GOLD }} />
              {cs.eyebrow || "Case Study"}
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl mt-6 leading-tight tracking-tight" style={{ color: "white" }}>
              {cs.title}
            </h2>
            <p className="text-base mt-5 leading-relaxed" style={{ color: "#94a3b8" }}>{cs.sub}</p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {(cs.cards || []).map((c, i) => (
              <div key={i} className="p-6 rounded-lg"
                   style={{ background: "rgba(255,255,255,0.04)",
                            border: "1px solid rgba(196,164,84,0.25)" }}
                   data-testid={`case-card-${i}`}>
                <div className="font-serif text-4xl sm:text-5xl leading-none tracking-tight" style={{ color: GOLD }}>
                  {c.metric}
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] mt-3 font-semibold" style={{ color: "#e2e1dd" }}>
                  {c.label}
                </div>
                <p className="text-sm mt-4 leading-relaxed" style={{ color: "#94a3b8" }}>{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    ),
    testimonials: () => s.testimonials?.enabled && (cfg.testimonials?.quotes || []).length > 0 && (
      <section key="testi" className="border-b" style={{ borderColor: BORDER, background: "white" }}
               data-testid="landing-testimonials" aria-label="Customer testimonials">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-[11px] uppercase tracking-[0.28em] font-semibold" style={{ color: GOLD }}>
              {cfg.testimonials?.eyebrow || "In their own words"}
            </div>
            <div className="font-serif text-3xl sm:text-4xl mt-4 leading-tight tracking-tight" style={{ color: NAVY }}>
              What our fund-ops teams tell us.
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {cfg.testimonials.quotes.map((q, i) => (
              <blockquote key={i} className="border rounded-lg p-8 relative"
                          style={{ borderColor: BORDER, background: "#faf9f5" }}
                          data-testid={`testimonial-${i}`}>
                <div className="font-serif text-6xl leading-none absolute -top-2 left-6 select-none"
                     style={{ color: GOLD, opacity: 0.35 }}>&ldquo;</div>
                <p className="font-serif text-lg sm:text-xl leading-relaxed pt-2" style={{ color: NAVY }}>
                  {q.quote}
                </p>
                <div className="mt-6 flex items-center gap-3">
                  {q.photo ? (
                    <img src={q.photo} alt={q.author} className="w-11 h-11 rounded-full object-cover"
                         style={{ border: `2px solid ${GOLD}` }} />
                  ) : (
                    <div className="w-11 h-11 rounded-full flex items-center justify-center font-serif text-sm"
                         style={{ background: NAVY, color: GOLD }}>
                      {(q.author || "").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("")}
                    </div>
                  )}
                  <div>
                    <div className="text-sm font-semibold" style={{ color: NAVY }}>{q.author}</div>
                    <div className="text-[11px]" style={{ color: MUTED }}>
                      {q.role}{q.firm ? ` \u00b7 ${q.firm}` : ""}
                    </div>
                  </div>
                </div>
              </blockquote>
            ))}
          </div>
        </div>
      </section>
    ),
  };

  const order = Array.isArray(cfg.section_order) && cfg.section_order.length > 0
                ? cfg.section_order
                : ["trust_stats", "logo_wall", "case_study", "testimonials"];
  return <>{order.map((k) => renderers[k] && renderers[k]())}</>;
}

/* ───────────────── atoms ───────────────── */
const Eyebrow = ({ children, dark = false, center = false }) => (
  <div
    className={`flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] font-semibold ${center ? "justify-center" : ""}`}
    style={{ color: dark ? "#94a3b8" : MUTED }}
  >
    <span className="inline-block w-8 h-[1px]" style={{ background: dark ? GOLD : NAVY }} />
    {children}
  </div>
);

/* ───────────────── hero live-console mock ───────────────── */
function ConsoleMock() {
  return (
    <div
      className="bg-white rounded-2xl border shadow-2xl overflow-hidden"
      style={{ borderColor: BORDER }}
    >
      {/* window chrome */}
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b" style={{ borderColor: BORDER, background: "#f5f4f0" }}>
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#e2e1dd" }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#e2e1dd" }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#e2e1dd" }} />
        <div className="ml-4 text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: MUTED }}>
          aifledger · category ii fund · fy 2026-27
        </div>
      </div>

      {/* body */}
      <div className="grid grid-cols-12 min-h-[440px]">
        {/* mini sidebar */}
        <div className="col-span-3 border-r py-4" style={{ borderColor: BORDER, background: NAVY }}>
          <div className="px-4 text-[9px] uppercase tracking-[0.2em] font-semibold text-white/40">Fund Ops</div>
          {["Dashboard", "Investors", "Capital Calls", "NAV", "Compliance", "Reports"].map((it, i) => (
            <div
              key={it}
              className="px-4 py-2 text-[11px] flex items-center gap-2"
              style={{ color: i === 2 ? "white" : "rgba(255,255,255,0.55)", background: i === 2 ? "rgba(196,164,84,0.14)" : "transparent", borderLeft: i === 2 ? `2px solid ${GOLD}` : "2px solid transparent" }}
            >
              <span className="w-1 h-1 rounded-full" style={{ background: i === 2 ? GOLD : "transparent" }} />
              {it}
            </div>
          ))}
        </div>

        {/* content */}
        <div className="col-span-9 p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: MUTED }}>
            Capital Call · CC-2026-04
          </div>
          <div className="font-serif text-2xl mt-1 leading-tight" style={{ color: NAVY }}>
            15% Drawdown — Closed
          </div>

          <div className="grid grid-cols-3 gap-3 mt-4">
            {[
              ["Called", "₹ 12.00 Cr"],
              ["Received", "₹ 10.80 Cr", true],
              ["Overdue", "₹ 1.20 Cr"],
            ].map(([k, v, ok]) => (
              <div key={k} className="p-3 rounded-md border" style={{ borderColor: BORDER }}>
                <div className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: MUTED }}>{k}</div>
                <div className="font-serif text-lg mt-1 leading-none" style={{ color: ok ? GOLD : NAVY }}>{v}</div>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-[9px] uppercase tracking-widest font-semibold mb-2" style={{ color: MUTED }}>
              LP Payments · Last 24h
            </div>
            <div className="space-y-1.5">
              {[
                ["Aarav Kapoor", "₹ 25.0 L", "UTR 8842…", "Reconciled"],
                ["Riya Trust", "₹ 40.0 L", "UTR 8843…", "Reconciled"],
                ["Bansal FO", "₹ 1.20 Cr", "—", "Awaiting"],
                ["Manav LLP", "₹ 15.0 L", "UTR 8850…", "Reconciled"],
              ].map((r, i) => (
                <div key={i} className="flex items-center justify-between text-[11px] px-2.5 py-1.5 rounded" style={{ background: i % 2 ? "transparent" : "#faf9f5" }}>
                  <div className="flex-1 font-medium" style={{ color: NAVY }}>{r[0]}</div>
                  <div className="w-24 text-right font-medium tabular-nums" style={{ color: NAVY }}>{r[1]}</div>
                  <div className="w-24 text-right" style={{ color: MUTED }}>{r[2]}</div>
                  <div className="w-20 text-right">
                    <span
                      className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider"
                      style={{
                        background: r[3] === "Reconciled" ? "#dcfce7" : "#fef9c3",
                        color: r[3] === "Reconciled" ? "#166534" : "#854d0e",
                      }}
                    >
                      {r[3]}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 text-[10px]" style={{ color: MUTED }}>
            <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#22c55e" }} />
            Live · Reconciling UTRs from bank statement
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────────────── lead form ───────────────── */
const CATEGORIES = ["Category I", "Category II", "Category III", "Not sure"];
const CORPUS_BANDS = ["< ₹ 50 Cr", "₹ 50 – 250 Cr", "₹ 250 Cr – 1,000 Cr", "> ₹ 1,000 Cr"];

function LeadForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", fund_name: "",
    category: "", corpus: "", message: "", website: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.category) delete payload.category;
      Object.keys(payload).forEach((k) => { if (!payload[k]) delete payload[k]; });
      const { data } = await api.post("/leads", payload);
      trackEvent("book_demo_submitted", {
        aif_category: form.category || null,
        corpus_band: form.corpus_band || null,
        fund_stage: form.fund_stage || null,
        has_message: !!form.message,
      });
      trackLandingEvent("convert");  // A/B conversion signal
      toast.success(data?.message || "Thanks — we'll reach out shortly.");
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : (typeof detail === "string" ? detail : "Could not submit. Please try again.");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className="border rounded-xl p-10 bg-white flex flex-col items-center text-center"
        style={{ borderColor: BORDER }}
        data-testid="lead-form-success"
      >
        <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: CREAM, border: `1px solid ${BORDER}` }}>
          <CheckCircle2 size={24} style={{ color: GOLD }} />
        </div>
        <div className="font-serif text-3xl mt-6" style={{ color: NAVY }}>
          Received. <span style={{ color: GOLD }}>Thank you.</span>
        </div>
        <p className="text-sm mt-4 max-w-md leading-relaxed" style={{ color: MUTED }}>
          A Corpzo AIFLedger specialist will reach out within one business day to
          schedule your walkthrough. In the meantime, feel free to sign in
          to the demo console.
        </p>
        <div className="mt-6 flex gap-3">
          <Link to="/login">
            <Button className="h-11 px-6 text-sm font-semibold rounded-md" style={{ background: NAVY, color: "white" }} data-testid="lead-form-signin">
              Sign in to console <ArrowRight size={14} className="ml-2" />
            </Button>
          </Link>
          <Button
            variant="outline"
            className="h-11 px-6 text-sm font-semibold bg-white rounded-md"
            style={{ borderColor: BORDER, color: NAVY }}
            onClick={() => { setForm({ name: "", email: "", phone: "", fund_name: "", category: "", corpus: "", message: "" }); setDone(false); }}
            data-testid="lead-form-reset"
          >
            Submit another
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="border rounded-xl p-6 sm:p-8 bg-white"
      style={{ borderColor: BORDER }}
      data-testid="lead-form"
    >
      <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>
        Book a demo · 30 minutes
      </div>
      <div className="font-serif text-2xl mt-2" style={{ color: NAVY }}>
        Tell us about your fund.
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        <div>
          <Label htmlFor="lead-name" className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: MUTED }}>Full name *</Label>
          <Input id="lead-name" data-testid="lead-form-name" required minLength={2}
                 value={form.name} onChange={upd("name")}
                 className="mt-1.5 bg-white" placeholder="Aarav Kapoor" />
        </div>
        <div>
          <Label htmlFor="lead-email" className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: MUTED }}>Work email *</Label>
          <Input id="lead-email" type="email" required data-testid="lead-form-email"
                 value={form.email} onChange={upd("email")}
                 className="mt-1.5 bg-white" placeholder="you@yourfund.com" />
        </div>
        <div>
          <Label htmlFor="lead-phone" className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: MUTED }}>Phone</Label>
          <Input id="lead-phone" data-testid="lead-form-phone"
                 value={form.phone} onChange={upd("phone")}
                 className="mt-1.5 bg-white" placeholder="+91 98XXX XXXXX" />
        </div>
        <div>
          <Label htmlFor="lead-fund" className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: MUTED }}>Fund / firm name</Label>
          <Input id="lead-fund" data-testid="lead-form-fund"
                 value={form.fund_name} onChange={upd("fund_name")}
                 className="mt-1.5 bg-white" placeholder="e.g. your fund's legal name" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: MUTED }}>AIF Category</Label>
          <Select value={form.category} onValueChange={upd("category")}>
            <SelectTrigger className="mt-1.5 bg-white" data-testid="lead-form-category" aria-label="Select AIF category">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c} data-testid={`lead-form-category-${c.replace(/\s+/g, "-").toLowerCase()}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: MUTED }}>Target corpus</Label>
          <Select value={form.corpus} onValueChange={upd("corpus")}>
            <SelectTrigger className="mt-1.5 bg-white" data-testid="lead-form-corpus" aria-label="Select corpus band">
              <SelectValue placeholder="Select band" />
            </SelectTrigger>
            <SelectContent>
              {CORPUS_BANDS.map((c, i) => (
                <SelectItem key={c} value={c} data-testid={`lead-form-corpus-${i}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4">
        <Label htmlFor="lead-msg" className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: MUTED }}>Anything specific?</Label>
        <Textarea id="lead-msg" rows={3} data-testid="lead-form-message"
                  value={form.message} onChange={upd("message")}
                  className="mt-1.5 bg-white"
                  placeholder="e.g. Migrating from Excel, need class-wise NAV & Form 64C by Nov." />
      </div>

      {/* Honeypot — hidden from users, visible to bots */}
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }} aria-hidden="true">
        <label>Website (do not fill)
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={upd("website")}
          />
        </label>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4 justify-between">
        <p className="text-[11px] max-w-sm" style={{ color: MUTED }}>
          By submitting, you agree to be contacted by the Corpzo team about your enquiry.
          See our <Link to="/privacy" className="underline hover:text-[#0b192c]" data-testid="lead-form-privacy-link">Privacy Policy</Link> · DPDP-aligned.
        </p>
            <a href="mailto:reach@aifledger.com?subject=Corpzo%20AIF%20OS%20Demo">
              <Button
                className="h-11 px-6 text-sm font-semibold rounded-md"
                style={{ background: "transparent", color: NAVY, border: `1px solid ${BORDER}` }}
                variant="outline"
                data-testid="lead-form-email-fallback"
              >
                <Mail size={14} className="mr-2" /> Email us instead
              </Button>
            </a>
            <Button
              type="submit"
              disabled={loading}
              className="h-12 px-8 text-sm font-semibold rounded-md"
              style={{ background: NAVY, color: "white" }}
              data-testid="lead-form-submit"
            >
              {loading ? "Sending…" : "Book my demo"}
              {!loading && <ArrowRight size={16} className="ml-2" />}
            </Button>
      </div>
    </form>
  );
}

/* ───────────────── Instant walkthrough tile ─────────────────
 * Prospect self-serves a category-tailored sandbox and lands straight
 * inside /dashboard. No waiting on Corpzo.
 */
function InstantWalkthrough() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({
    prospect_name: "", prospect_email: "", company: "",
    category: "Category II", sub_category: "", structure: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/demos/categories")
      .then(({ data }) => setMeta(data))
      .catch(() => setMeta(null));
  }, []);

  const cat = meta?.categories?.find((c) => c.value === form.category);
  useEffect(() => {
    if (!cat) return;
    setForm((f) => ({
      ...f,
      sub_category: cat.sub_categories.includes(f.sub_category)
        ? f.sub_category : cat.default_sub,
      structure: cat.structures.includes(f.structure)
        ? f.structure : cat.default_structure,
    }));
  }, [cat]);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.prospect_name.trim() || !form.prospect_email.trim()) {
      toast.error("Enter your name and email");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/demos/instant", form);
      trackEvent("instant_demo_created", {
        category: data.category, sub_category: data.sub_category, structure: data.structure,
      });
      toast.success(`Provisioning ${data.fund_name}…`);
      // Auto-login into the freshly created demo tenant, then land on /dashboard.
      await api.post("/auth/login", data.auto_login);
      toast.success("Signed in — enjoy the walkthrough.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start your demo — please try the form on the right.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="relative rounded-xl p-6 sm:p-8 overflow-hidden"
      style={{ background: NAVY, color: "white" }}
      data-testid="instant-walkthrough"
    >
      <div className="absolute right-0 top-0 bottom-0 w-1" style={{ background: GOLD }} />
      <div className="text-[10px] uppercase tracking-[0.24em] font-semibold flex items-center gap-2"
           style={{ color: GOLD }}>
        <Rocket size={12} /> Instant walkthrough · 3-day sandbox
      </div>
      <div className="font-serif text-2xl sm:text-3xl mt-2 leading-tight">
        Or, step inside right now.
      </div>
      <p className="text-sm mt-3 text-white/70 leading-relaxed max-w-md">
        Pick your AIF category — we&apos;ll spin up a private sandbox with a fund,
        LPs, capital calls and NAV history that match your registration path.
        You&apos;ll be signed in before you finish this sentence.
      </p>

      <div className="grid sm:grid-cols-2 gap-3 mt-6">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-white/60">Full name *</label>
          <input required minLength={2}
                 value={form.prospect_name}
                 onChange={(e) => setForm({ ...form, prospect_name: e.target.value })}
                 placeholder="Aarav Kapoor"
                 className="w-full mt-1 rounded px-3 py-2 text-sm bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:border-white/60"
                 data-testid="instant-name" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-white/60">Work email *</label>
          <input type="email" required
                 value={form.prospect_email}
                 onChange={(e) => setForm({ ...form, prospect_email: e.target.value })}
                 placeholder="you@yourfund.com"
                 className="w-full mt-1 rounded px-3 py-2 text-sm bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:border-white/60"
                 data-testid="instant-email" />
        </div>
      </div>

      <div className="mt-3">
        <label className="text-[10px] uppercase tracking-wider font-semibold text-white/60">Company / fund name</label>
        <input value={form.company}
               onChange={(e) => setForm({ ...form, company: e.target.value })}
               placeholder="e.g. your fund's legal name (optional)"
               className="w-full mt-1 rounded px-3 py-2 text-sm bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:border-white/60"
               data-testid="instant-company" />
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mt-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-white/60">AIF Category</label>
          <select value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full mt-1 rounded px-3 py-2 text-sm bg-white/10 border border-white/20 text-white focus:outline-none focus:border-white/60 [&_option]:text-[#0b192c]"
                  data-testid="instant-category">
            {(meta?.categories || [{value:"Category I"},{value:"Category II"},{value:"Category III"}])
              .map((c) => <option key={c.value} value={c.value}>{c.value}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-white/60">Sub-category</label>
          <select value={form.sub_category}
                  onChange={(e) => setForm({ ...form, sub_category: e.target.value })}
                  className="w-full mt-1 rounded px-3 py-2 text-sm bg-white/10 border border-white/20 text-white focus:outline-none focus:border-white/60 [&_option]:text-[#0b192c]"
                  data-testid="instant-sub">
            {(cat?.sub_categories || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-white/60">Structure</label>
          <select value={form.structure}
                  onChange={(e) => setForm({ ...form, structure: e.target.value })}
                  disabled={(cat?.structures || []).length < 2}
                  className="w-full mt-1 rounded px-3 py-2 text-sm bg-white/10 border border-white/20 text-white focus:outline-none focus:border-white/60 disabled:opacity-60 [&_option]:text-[#0b192c]"
                  data-testid="instant-structure">
            {(cat?.structures || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {cat?.hint && (
        <div className="mt-3 text-[11px] text-white/50 leading-relaxed" data-testid="instant-hint">
          <b style={{ color: GOLD }}>{cat.value}:</b> {cat.hint}
          &nbsp;·&nbsp; NAV {cat.nav_frequency}
          &nbsp;·&nbsp; Waterfall {cat.waterfall}
        </div>
      )}

      <Button type="submit" disabled={loading}
              className="mt-5 h-12 w-full text-sm font-semibold rounded-md"
              style={{ background: GOLD, color: NAVY }}
              data-testid="instant-submit">
        {loading ? "Spinning up your sandbox…" : "Take the walkthrough now"}
        {!loading && <ArrowRight size={16} className="ml-2" />}
      </Button>
      <p className="mt-3 text-[10px] text-white/40 text-center">
        3-day expiry · fully isolated · no card required
      </p>
    </form>
  );
}

/* ───────────────── main ───────────────── */
export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      // `?preview=1` lets the Owner CMS iframe render the real landing page
      // while logged in — otherwise we auto-redirect to the app dashboard.
      const isPreview = typeof window !== "undefined"
        && new URLSearchParams(window.location.search).get("preview") === "1";
      if (isPreview) return;
      navigate(user.active_role === "investor" ? "/investor" : "/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    trackEvent("landing_viewed", { path: window.location.pathname });
  }, []);

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* ═════════════ Nav ═════════════ */}
      <nav
        className="sticky top-0 z-40 border-b backdrop-blur-md"
        style={{ borderColor: BORDER, background: "rgba(253,252,248,0.9)" }}
        data-testid="landing-nav"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-nav-brand" aria-label="AIFLedger — home">
            <PlatformLogo className="h-10 w-10" width={40} height={40} />
            <div className="hidden sm:block">
              <PlatformWordmarkImg className="h-7" />
              <div className="text-[9px] uppercase tracking-[0.2em] mt-1 font-semibold" style={{ color: MUTED }}>
                by Corpzo · Fund Admin
              </div>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm" style={{ color: MUTED }}>
            <a href="#platform" className="hover:text-[#0b192c]" data-testid="landing-nav-platform">Platform</a>
            <a href="#categories" className="hover:text-[#0b192c]" data-testid="landing-nav-categories">Categories</a>
            <a href="#modules" className="hover:text-[#0b192c]" data-testid="landing-nav-modules">Modules</a>
            <a href="#security" className="hover:text-[#0b192c]" data-testid="landing-nav-security">Security</a>
            <a href="#contact" className="hover:text-[#0b192c]" data-testid="landing-nav-contact">Contact</a>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login" aria-label="Sign in to AIFLedger">
              <Button
                variant="ghost"
                className="hidden sm:inline-flex text-sm font-semibold"
                style={{ color: NAVY }}
                data-testid="landing-nav-signin"
              >
                Sign in
              </Button>
            </Link>
            <a href="#contact">
              <Button
                className="text-sm font-semibold h-9 rounded-md"
                style={{ background: NAVY, color: "white" }}
                data-testid="landing-nav-demo"
              >
                Book a demo
              </Button>
            </a>
          </div>
        </div>
      </nav>

      <main id="main" data-testid="landing-main">

      {/* ═════════════ Hero ═════════════ */}
      <section id="platform" className="landing-grid relative overflow-hidden">
        <div className="landing-glow absolute inset-0 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 lg:pb-28 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <Eyebrow>AIFLedger by Corpzo · India</Eyebrow>
              <h1
                className="font-serif mt-8 leading-[1.02] tracking-tight"
                style={{ color: NAVY, fontSize: "clamp(2.5rem, 5.4vw, 4.75rem)" }}
              >
                Fund Administration<br />
                Platform for <span style={{ color: GOLD }}>SEBI-Registered</span><br />
                AIFs.
              </h1>
              <p className="text-base lg:text-lg mt-8 max-w-xl leading-relaxed" style={{ color: MUTED }}>
                The book of record for Indian Alternative Investment Funds — NAV,
                fund accounting, investor onboarding, capital calls, SEBI AIF
                compliance and LP reporting on one institutional-grade console.
                Built for Category I, II &amp; III AIFs.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <a href="#contact">
                  <Button
                    className="h-12 px-6 text-sm font-semibold rounded-md"
                    style={{ background: NAVY, color: "white" }}
                    data-testid="landing-hero-demo"
                  >
                    Book a demo
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                </a>
                <Link to="/login">
                  <Button
                    variant="outline"
                    className="h-12 px-6 text-sm font-semibold bg-white rounded-md"
                    style={{ borderColor: NAVY, color: NAVY }}
                    data-testid="landing-hero-signin"
                  >
                    Sign in to console
                  </Button>
                </Link>
              </div>

              <Link to="/trust"
                    className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs group"
                    style={{ color: MUTED }}
                    data-testid="landing-hero-badges"
                    aria-label="View Trust Center">
                <div className="flex items-center gap-2 group-hover:text-[#0b192c] transition">
                  <ShieldCheck size={14} style={{ color: GOLD }} /> SEBI-aligned
                </div>
                <div className="flex items-center gap-2 group-hover:text-[#0b192c] transition">
                  <Lock size={14} style={{ color: GOLD }} /> DPDP-ready
                </div>
                <div className="flex items-center gap-2 group-hover:text-[#0b192c] transition">
                  <Boxes size={14} style={{ color: GOLD }} /> SOC 2 in progress
                </div>
                <div className="flex items-center gap-2 group-hover:text-[#0b192c] transition">
                  <Zap size={14} style={{ color: GOLD }} /> AES-256 at rest
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold group-hover:text-[#0b192c] transition" style={{ color: NAVY }}>
                  Trust Center →
                </div>
              </Link>
            </div>

            <div className="lg:col-span-6">
              <ConsoleMock />
            </div>
          </div>
        </div>
      </section>

      {/* ═════════════ CMS sections (trust strip / logos / case / testimonials) ═════════════ */}
      <LandingCMSSections />


      {/* ═════════════ Capabilities (SEO H2 anchors) ═════════════ */}
      <section id="capabilities" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24" data-testid="landing-capabilities">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
          <div className="lg:col-span-7">
            <Eyebrow>What you get</Eyebrow>
            <div className="font-serif text-4xl sm:text-5xl mt-6 leading-tight tracking-tight" style={{ color: NAVY }}>
              One platform. <span style={{ color: GOLD }}>Every AIF back-office job.</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base leading-relaxed" style={{ color: MUTED }}>
              AIF fund accounting software India, NAV calculation, investor
              servicing, capital calls and SEBI compliance — under one console.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {[
            {
              h: "NAV Calculation & Fund Accounting",
              body: "Class-wise NAV, high-water mark, hurdle rate and side-pocket accounting for daily, weekly and monthly cycles. An Ind AS-aligned journal engine with maker-checker controls — the NAV calculation software for AIF that closes your month in hours, not weeks.",
            },
            {
              h: "Investor Onboarding & Capital Calls",
              body: "A complete AIF investor onboarding platform — PAN, Aadhaar, FATCA/CRS, subscription agreements and nominee capture. Issue capital calls in seconds, reconcile UTRs from Indian bank statements, and track drawdowns per commitment.",
            },
            {
              h: "SEBI Compliance & LP Reporting",
              body: "Purpose-built SEBI AIF compliance software: Form 64C tax pass-through, SEBI quarterly returns, PPM disclosures, valuation policy adherence and trustee reports. AIF investor servicing software delivers statements-of-account, capital-call notices and distribution advice through the LP portal.",
            },
            {
              h: "Built for Category I, II & III AIFs",
              body: "The only AIF back office software that speaks the regulatory grammar of every AIF category — VC drawdowns, PE class-wise economics, or Category III long-short NAV cycles with HWM and hurdle. Includes an AIF transfer agency software module for the unit register and register-of-members.",
            },
          ].map((c) => (
            <article
              key={c.h}
              className="border rounded-xl p-7 bg-white"
              style={{ borderColor: BORDER }}
              data-testid={`landing-cap-${c.h.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`}
            >
              <h2 className="font-serif text-2xl leading-tight" style={{ color: NAVY }}>{c.h}</h2>
              <p className="text-sm mt-3 leading-relaxed" style={{ color: MUTED }}>{c.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ═════════════ Category-native · Cat I / II / III ═════════════ */}
      <section id="categories" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Eyebrow>Category-native</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl mt-6 leading-tight tracking-tight" style={{ color: NAVY }}>
              Every AIF category. <span style={{ color: GOLD }}>One console.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base leading-relaxed" style={{ color: MUTED }}>
              AIFLedger speaks the regulatory grammar of Category I, II and III
              funds — from close-ended VC drawdowns to open-ended long-short NAV cycles.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mt-14">
          {[
            {
              tag: "Category I",
              icon: Briefcase,
              title: "Venture, SME, Infrastructure & Social Venture Funds",
              points: ["Close-ended drawdown model", "Vintage & series tracking", "Investee cap-table & rights", "Realisation waterfall"],
            },
            {
              tag: "Category II",
              icon: Coins,
              title: "Private Equity, Credit, Real Estate & Fund-of-Funds",
              points: ["Class-wise economics", "Coupon & CCD/CCPS tracking", "Distribution waterfall & carry", "Side-pockets for illiquid holdings"],
            },
            {
              tag: "Category III",
              icon: LineChart,
              title: "Long-Short, Quant, Derivatives & Hedge Strategies",
              points: ["Daily/weekly NAV cycles", "HWM + hurdle economics", "Exposure & leverage limits", "Open-ended subs & redemptions"],
            },
          ].map((c) => (
            <div
              key={c.tag}
              className="border rounded-xl p-7 bg-white flex flex-col"
              style={{ borderColor: BORDER }}
              data-testid={`landing-cat-${c.tag.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center justify-between">
                <span
                  className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2.5 py-1 rounded-full"
                  style={{ background: CREAM, color: NAVY, border: `1px solid ${BORDER}` }}
                >
                  {c.tag}
                </span>
                <c.icon size={18} style={{ color: GOLD }} />
              </div>
              <div className="font-serif text-2xl mt-6 leading-tight" style={{ color: NAVY }}>{c.title}</div>
              <ul className="mt-5 space-y-2 flex-1">
                {c.points.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm" style={{ color: MUTED }}>
                    <CheckCircle2 size={14} style={{ color: GOLD }} className="mt-0.5 flex-shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ═════════════ Modules · bento mosaic ═════════════ */}
      <section id="modules" className="py-24 lg:py-28" style={{ background: "white", borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <Eyebrow>What&apos;s inside</Eyebrow>
              <h2 className="font-serif text-4xl sm:text-5xl mt-6 leading-tight tracking-tight" style={{ color: NAVY }}>
                Twelve back-office jobs.<br />
                <span style={{ color: GOLD }}>One workflow engine.</span>
              </h2>
            </div>
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mt-14">
            {/* Large tile — Investor Onboarding */}
            <div className="md:col-span-4 border rounded-xl p-8 flex flex-col justify-between min-h-[280px]" style={{ borderColor: BORDER, background: CREAM }}>
              <div>
                <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: NAVY }}>
                  <Users size={16} className="text-white" />
                </div>
                <div className="font-serif text-3xl mt-6" style={{ color: NAVY }}>
                  Investor Onboarding
                </div>
                <p className="text-sm mt-3 max-w-md leading-relaxed" style={{ color: MUTED }}>
                  Wizard from lead capture to unit allotment. PAN-OCR, live CKYC pull, FATCA/PEP/UBO
                  declarations, sanction screening, e-sign and per-class commitment routing.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 mt-6">
                {["PAN OCR", "CKYC", "FATCA/CRS", "Sanctions", "E-Sign"].map((t) => (
                  <span key={t} className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full bg-white" style={{ color: NAVY, border: `1px solid ${BORDER}` }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Capital Calls */}
            <div className="md:col-span-2 border rounded-xl p-6 min-h-[280px]" style={{ borderColor: BORDER, background: NAVY, color: "white" }}>
              <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.1)" }}>
                <Wallet size={16} style={{ color: GOLD }} />
              </div>
              <div className="font-serif text-2xl mt-6 leading-tight">Capital Calls & Drawdowns</div>
              <p className="text-xs mt-3 text-white/70 leading-relaxed">
                Draft, dispatch, UTR-match, penalty accrual, equalisation across closings.
              </p>
            </div>

            {/* NAV */}
            <div className="md:col-span-2 border rounded-xl p-6 min-h-[240px]" style={{ borderColor: BORDER }}>
              <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: CREAM, border: `1px solid ${BORDER}` }}>
                <LineChart size={16} style={{ color: NAVY }} />
              </div>
              <div className="font-serif text-xl mt-5" style={{ color: NAVY }}>NAV & Valuation</div>
              <p className="text-xs mt-2 leading-relaxed" style={{ color: MUTED }}>
                Class-wise NAV, HWM, MTM, side-pockets, valuation approval workflow.
              </p>
            </div>

            {/* Fund Accounting */}
            <div className="md:col-span-2 border rounded-xl p-6 min-h-[240px]" style={{ borderColor: BORDER }}>
              <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: CREAM, border: `1px solid ${BORDER}` }}>
                <BookOpen size={16} style={{ color: NAVY }} />
              </div>
              <div className="font-serif text-xl mt-5" style={{ color: NAVY }}>Fund Accounting</div>
              <p className="text-xs mt-2 leading-relaxed" style={{ color: MUTED }}>
                Multi-book ledger, maker-checker journals, expense allocation, trial balance.
              </p>
            </div>

            {/* Compliance */}
            <div className="md:col-span-2 border rounded-xl p-6 min-h-[240px]" style={{ borderColor: BORDER, background: CREAM }}>
              <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "white", border: `1px solid ${BORDER}` }}>
                <CalendarDays size={16} style={{ color: NAVY }} />
              </div>
              <div className="font-serif text-xl mt-5" style={{ color: NAVY }}>Compliance Calendar</div>
              <p className="text-xs mt-2 leading-relaxed" style={{ color: MUTED }}>
                SEBI filings, PPM audits, KYC refresh, trustee & investor consent tracking.
              </p>
            </div>

            {/* Reporting */}
            <div className="md:col-span-3 border rounded-xl p-7 min-h-[220px]" style={{ borderColor: BORDER }}>
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: NAVY }}>
                  <FileBarChart size={16} className="text-white" />
                </div>
                <span className="text-[9px] uppercase tracking-widest font-semibold" style={{ color: GOLD }}>PDF · Excel</span>
              </div>
              <div className="font-serif text-2xl mt-5" style={{ color: NAVY }}>Reporting Engine</div>
              <p className="text-sm mt-3 leading-relaxed" style={{ color: MUTED }}>
                Statement of account, capital call, distribution notice, Form 64C/D, TDS, CAS,
                NAV pack — Corpzo-branded or LP-branded, one-click export.
              </p>
            </div>

            {/* LP Portal */}
            <div className="md:col-span-3 border rounded-xl p-7 min-h-[220px]" style={{ borderColor: BORDER, background: NAVY, color: "white" }}>
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.1)" }}>
                  <ScrollText size={16} style={{ color: GOLD }} />
                </div>
                <span className="text-[9px] uppercase tracking-widest font-semibold text-white/50">Family-office grade</span>
              </div>
              <div className="font-serif text-2xl mt-5">LP Portal</div>
              <p className="text-sm mt-3 leading-relaxed text-white/70">
                Investor login for commitments, NAV, units, distributions, tax packs — with
                self-service redemption and consent capture.
              </p>
            </div>
          </div>

          {/* extra module row */}
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            {[
              { icon: GitBranch, t: "Deal Pipeline & IC", b: "Investment memo, IC approval, closing conditions and covenants." },
              { icon: Layers, t: "Multi-Class Funds", b: "Series & class-wise economics, exit-load engine, side-pocket units." },
              { icon: FileText, t: "Document Vault", b: "Versioned, tagged, audit-logged — SEBI-ready in one click." },
            ].map((m) => (
              <div key={m.t} className="border rounded-xl p-6" style={{ borderColor: BORDER }}>
                <m.icon size={16} style={{ color: GOLD }} />
                <div className="font-serif text-lg mt-3" style={{ color: NAVY }}>{m.t}</div>
                <p className="text-xs mt-1.5 leading-relaxed" style={{ color: MUTED }}>{m.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═════════════ Who benefits ═════════════ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <Eyebrow>For your team</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl mt-6 leading-tight tracking-tight" style={{ color: NAVY }}>
              A workspace for every seat<br />in the fund.
            </h2>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-14">
          {[
            {
              icon: Building2, t: "Fund Managers",
              b: "AUM, dry powder, deal pipeline, portfolio exposure and NAV — actionable on one dashboard.",
            },
            {
              icon: BookOpen, t: "Accountants",
              b: "Multi-book ledger, journal maker-checker, NAV, trial balance, B/S, P&L and audit schedules.",
            },
            {
              icon: ScrollText, t: "Compliance Officers",
              b: "SEBI filing tracker, PPM audit trail, KYC refresh, investor consent and evidence pack.",
            },
            {
              icon: Users, t: "Investors / LPs",
              b: "Commitment, NAV, statements, distribution & tax packs — with self-service redemption.",
            },
          ].map((r) => (
            <div key={r.t} className="border rounded-xl p-6 bg-white" style={{ borderColor: BORDER }} data-testid={`landing-role-${r.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: NAVY }}>
                <r.icon size={16} className="text-white" />
              </div>
              <div className="font-serif text-2xl mt-5" style={{ color: NAVY }}>{r.t}</div>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: MUTED }}>{r.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═════════════ Security · dark checklist ═════════════ */}
      <section id="security" className="landing-grid-dark py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5">
            <Eyebrow dark>Security & Controls</Eyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl mt-6 leading-tight tracking-tight text-white">
              Built for auditors,<br />
              <span style={{ color: GOLD }}>trusted by trustees.</span>
            </h2>
            <p className="text-sm mt-6 leading-relaxed text-white/60 max-w-md">
              AIFLedger is architected around evidence — every material action produces a signed audit line,
              every dataset is tenant-scoped, every workflow needs a maker and a checker.
            </p>
            <div className="mt-8 flex items-center gap-4 text-xs text-white/50">
              <ShieldCheck size={14} style={{ color: GOLD }} /> SEBI-aligned · DPDP-ready · SOC-style controls
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
            {[
              ["Role-based access control", "13 pre-built roles × per-action permissions across every module."],
              ["Maker-checker workflow", "Journals, NAV, capital calls, distributions and allotments — dual-approval by design."],
              ["Immutable audit log", "Old-value / new-value capture on every mutation, plus IP, device and timestamp."],
              ["MFA & session policies", "TOTP two-factor, password rotation policy, session timeout, brute-force lockout."],
              ["Multi-tenant isolation", "Client, fund, scheme and investor scoping — no cross-tenant data reads."],
              ["DPDP-aligned consent", "Consent capture, retention policy, right-to-erasure workflow for LP data."],
            ].map(([t, b]) => (
              <div key={t} className="p-4 rounded-lg border border-white/10 bg-white/[0.03] flex items-start gap-3">
                <CheckCircle2 size={16} style={{ color: GOLD }} className="mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-white">{t}</div>
                  <div className="text-xs mt-1 text-white/60 leading-relaxed">{b}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═════════════ CTA + Lead form + Contact ═════════════ */}
      <section id="contact" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-28">
        <div className="text-center max-w-3xl mx-auto">
          <Eyebrow center>Talk to us</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-6xl mt-8 leading-tight tracking-tight" style={{ color: NAVY }}>
            Ready to run your fund<br />
            <span style={{ color: GOLD }}>on a real operating system?</span>
          </h2>
          <p className="text-base lg:text-lg mt-8 leading-relaxed" style={{ color: MUTED }}>
            Book a 30-minute walkthrough. Share a few details and the Corpzo team will reach out within one business day.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 mt-14">
          {/* Instant self-serve (left) */}
          <div className="lg:col-span-5">
            <InstantWalkthrough />
          </div>

          {/* Lead form (right) */}
          <div className="lg:col-span-7">
            <LeadForm />
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 mt-8">
          {/* Contact info + reassurance */}
          <aside className="lg:col-span-12 space-y-4">
            <div className="p-6 rounded-xl border" style={{ borderColor: BORDER, background: NAVY, color: "white" }}>
              <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>
                Direct line
              </div>
              <div className="font-serif text-2xl mt-3">
                Speak to a fund-admin specialist.
              </div>
              <p className="text-sm mt-3 text-white/70 leading-relaxed">
                Prefer to skip the form? Reach us directly — replies typically within one business day.
              </p>
            </div>

            {[
              { icon: Mail, l: "Email", v: "reach@aifledger.com", href: "mailto:reach@aifledger.com" },
              { icon: Phone, l: "Phone", v: "+91 99991 39391", href: "tel:+919999139391" },
              { icon: MapPin, l: "Office", v: "Noida, India", href: null },
            ].map((c) => {
              const inner = (
                <div className="flex items-start gap-4 p-5 border rounded-lg bg-white hover:shadow-md transition-shadow" style={{ borderColor: BORDER }}>
                  <div className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: CREAM }}>
                    <c.icon size={16} style={{ color: NAVY }} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold" style={{ color: MUTED }}>{c.l}</div>
                    <div className="text-sm mt-1 font-medium" style={{ color: NAVY }}>{c.v}</div>
                  </div>
                </div>
              );
              return c.href ? (
                <a key={c.l} href={c.href} data-testid={`landing-contact-${c.l.toLowerCase()}`}>{inner}</a>
              ) : (
                <div key={c.l} data-testid={`landing-contact-${c.l.toLowerCase()}`}>{inner}</div>
              );
            })}

            <div className="p-4 rounded-lg border flex items-start gap-3" style={{ borderColor: BORDER, background: CREAM }}>
              <Lock size={14} style={{ color: GOLD }} className="mt-0.5 flex-shrink-0" />
              <p className="text-xs leading-relaxed" style={{ color: MUTED }}>
                Your details are used only to schedule a walkthrough. DPDP-aligned handling · no marketing lists.
              </p>
            </div>
          </aside>
        </div>
      </section>

      {/* ═════════════ Footer ═════════════ */}
      </main>

      <footer className="border-t" style={{ borderColor: BORDER, background: "white" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid gap-8 lg:grid-cols-12 items-start">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <PlatformLogo className="h-9 w-9" width={36} height={36} />
              <div>
                <PlatformWordmarkImg className="h-6" />
                <div className="text-[9px] uppercase tracking-[0.2em] mt-1 font-semibold" style={{ color: MUTED }}>
                  by Corpzo · Fund Admin
                </div>
              </div>
            </div>
            <p className="text-xs mt-4 leading-relaxed max-w-xs" style={{ color: MUTED }}>
              Institutional fund administration for SEBI-registered Alternative Investment Funds in India.
            </p>
          </div>
          <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-6 text-xs">
            {[
              { h: "Platform", items: [
                ["Categories", "#categories"],
                ["Modules", "#modules"],
                ["Security", "#security"],
                ["Book a demo", "#contact"],
              ] },
              { h: "Company", items: [
                ["About Corpzo", "https://corpzo.com/about"],
                ["Careers", "https://corpzo.com/careers"],
                ["Contact us", "#contact"],
                ["Sign in", "/login"],
              ] },
              { h: "Resources", items: [
                ["Guides & articles", "/resources/"],
                ["Trust Center", "/trust"],
                ["Documentation", "https://docs.aifledger.com"],
                ["Status", "https://status.aifledger.com"],
              ] },
              { h: "Legal", items: [
                ["Terms of Service", "/terms"],
                ["Privacy Policy", "/privacy"],
                ["Cookie preferences", "cookie-prefs"],
                ["DPA on request", "mailto:dpo@aifledger.com"],
              ] },
            ].map((col) => (
              <div key={col.h}>
                <div className="uppercase tracking-[0.18em] font-semibold" style={{ color: NAVY }}>{col.h}</div>
                <ul className="mt-3 space-y-2">
                  {col.items.map(([label, href]) => (
                    <li key={label}>
                      {href === "cookie-prefs" ? (
                        <button
                          onClick={() => window.dispatchEvent(new Event("aif-open-cookie-prefs"))}
                          className="hover:text-[#0b192c] text-left"
                          style={{ color: MUTED }}
                          data-testid="footer-cookie-prefs"
                        >
                          {label}
                        </button>
                      ) : href ? (
                        href.startsWith("#") ? (
                          <a href={href} className="hover:text-[#0b192c]" style={{ color: MUTED }}>{label}</a>
                        ) : href.startsWith("/resources") || href.endsWith(".html") ? (
                          // Static HTML pages under /public — force a real HTTP navigation,
                          // otherwise React Router intercepts and falls through to Landing.
                          <a href={href} className="hover:text-[#0b192c]" style={{ color: MUTED }}>{label}</a>
                        ) : href.startsWith("/") ? (
                          <Link to={href} className="hover:text-[#0b192c]" style={{ color: MUTED }}>{label}</Link>
                        ) : (
                          <a href={href}
                             className="hover:text-[#0b192c]" style={{ color: MUTED }}
                             {...(href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
                            {label}
                          </a>
                        )
                      ) : (
                        <span style={{ color: MUTED }}>{label}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="lg:col-span-12 border-t pt-6 grid gap-4 sm:grid-cols-2" style={{ borderColor: BORDER }}>
            <div className="text-[11px]" style={{ color: MUTED }}>
              <div className="uppercase tracking-[0.18em] font-semibold mb-2" style={{ color: NAVY }}>Contact</div>
              <div><a href="mailto:reach@aifledger.com" className="hover:text-[#0b192c]">reach@aifledger.com</a></div>
              <div><a href="tel:+919999139391" className="hover:text-[#0b192c]">+91 99991 39391</a> · Noida, India</div>
            </div>
            <div className="text-[11px]" style={{ color: MUTED }}>
              <div className="uppercase tracking-[0.18em] font-semibold mb-2" style={{ color: NAVY }}>Powered by</div>
              <div className="uppercase tracking-[0.18em] font-semibold" style={{ color: NAVY }}>
                <a href="https://corpzo.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#c4a454]">POWERED BY CORPZO</a>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t" style={{ borderColor: BORDER }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 text-[11px] leading-relaxed" style={{ color: MUTED }}>
            <span className="font-semibold" style={{ color: NAVY }}>AIF fund administration software</span>
            {" | "}Fund accounting for SEBI-registered AIFs
            {" | "}NAV calculation &amp; investor servicing platform
          </div>
        </div>
        <div className="border-t" style={{ borderColor: BORDER }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col sm:flex-row gap-2 items-center justify-between text-[11px]" style={{ color: MUTED }}>
            <div>© {new Date().getFullYear()} AIFLedger · All rights reserved</div>
            <div className="flex items-center gap-2">
              <Sparkles size={12} style={{ color: GOLD }} /> SEBI-aligned · DPDP-ready · Multi-tenant
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
